import React, { useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/config';

const decodeBase64UrlJson = (s) => {
  try {
    const b64 = String(s).replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const json = decodeURIComponent(escape(atob(padded)));
    return JSON.parse(json);
  } catch {
    return null;
  }
};

export default function VoucherUploadFromOtherDevice() {
  const { token: tokenParam } = useParams();
  const [searchParams] = useSearchParams();
  const packed = searchParams.get('d');

  const packedDetails = useMemo(() => (packed ? decodeBase64UrlJson(packed) : null), [packed]);
  const token = tokenParam || packedDetails?.token || '';

  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('PENDING');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const publicApi = useMemo(
    () =>
      axios.create({
        baseURL: API_BASE_URL,
      }),
    []
  );

  const refresh = async () => {
    if (!token) return;
    setError('');
    try {
      const resp = await publicApi.get(`/public/voucher-upload-sessions/${encodeURIComponent(token)}`);
      const payload = resp?.data?.data || resp?.data || {};
      setStatus(payload.status || 'PENDING');
      if (payload.message) setMessage(payload.message);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to fetch upload status.');
    }
  };

  const upload = async () => {
    if (!token) {
      setError('Missing upload token.');
      return;
    }
    if (!file) {
      setError('Please choose a file to upload.');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const form = new FormData();
      form.append('file', file);

      const resp = await publicApi.post(
        `/public/voucher-upload-sessions/${encodeURIComponent(token)}/file`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const payload = resp?.data?.data || resp?.data || {};
      setStatus(payload.status || 'UPLOADED');
      setMessage(payload.message || 'Upload successful. You can go back to the first device and press Refresh.');
      setFile(null);
    } catch (err) {
      setError(err?.response?.data?.message || 'Upload failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-120px)] px-4 py-10">
      <div className="max-w-xl mx-auto bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
        <div className="px-6 py-5 bg-gradient-to-r from-primary-600 to-primary-700">
          <h1 className="text-xl sm:text-2xl font-bold text-white">Upload payment voucher</h1>
          <p className="text-primary-100 text-sm mt-1">Upload your bank transfer screenshot/voucher for verification.</p>
        </div>

        <div className="p-6 space-y-5">
          {!token && (
            <div className="p-4 bg-red-50 border-2 border-red-200 text-red-700 rounded-xl text-sm">
              Invalid link (missing token). Please rescan the QR from the donation form.
            </div>
          )}

          {!!token && (
            <>
              <div className="p-4 rounded-xl border border-gray-200 bg-gray-50 text-sm space-y-1">
                <div className="flex justify-between gap-3">
                  <span className="text-gray-600">Status</span>
                  <span className="font-bold text-gray-900">{status}</span>
                </div>
                {packedDetails?.amount ? (
                  <div className="flex justify-between gap-3">
                    <span className="text-gray-600">Amount</span>
                    <span className="font-semibold text-gray-900">NPR {packedDetails.amount}</span>
                  </div>
                ) : null}
              </div>

              {error && (
                <div className="p-4 bg-red-50 border-2 border-red-200 text-red-700 rounded-xl text-sm">{error}</div>
              )}
              {message && (
                <div className="p-4 bg-green-50 border-2 border-green-200 text-green-700 rounded-xl text-sm">{message}</div>
              )}

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-800">
                  Payment voucher / screenshot <span className="text-red-500">*</span>
                </label>

                <label className="flex flex-col items-center px-4 py-5 bg-white rounded-xl shadow-sm tracking-wide border cursor-pointer hover:bg-gray-50 transition border-dashed border-gray-400">
                  <svg className="w-7 h-7 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M16.88 9.1A4 4 0 0 1 16 17H5a5 5 0 0 1-1-9.9V7a3 3 0 0 1 4.52-2.59A4.98 4.98 0 0 1 17 8c5 5 5 16.88 9.1zM10 14a1 1 0 0 0 1-1V9h2l-3-3-3 3h2v4a1 1 0 0 0 1 1z" />
                  </svg>
                  <span className="mt-2 text-sm leading-normal text-gray-600">
                    {file ? file.name : 'Choose image/PDF to upload'}
                  </span>
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*,application/pdf"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                  />
                </label>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={refresh}
                  className="flex-1 px-4 py-3 rounded-xl bg-white border border-gray-200 text-gray-800 font-semibold hover:bg-gray-50 transition"
                  disabled={!token || loading}
                >
                  Refresh
                </button>
                <button
                  type="button"
                  onClick={upload}
                  className={`flex-1 px-4 py-3 rounded-xl font-bold transition ${
                    loading || !token ? 'bg-gray-400 text-white cursor-not-allowed' : 'bg-primary-600 text-white hover:bg-primary-700'
                  }`}
                  disabled={!token || loading}
                >
                  {loading ? 'Uploading…' : 'Upload'}
                </button>
              </div>

              <p className="text-xs text-gray-500 pt-1">
                After uploading, go back to the original device and press “Refresh” in the donation form.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

